import { Checkbox, Flex, Typography } from 'antd';
import DrawerSectionEcho from '@/components/drawers/DownloadDrawer/DrawerSectionEcho';
import type {
  ContentPerformanceDownloadSettingsState,
  ContentPerformanceExportFileKey,
  ContentPerformanceReportsSectionProps,
} from './contentPerformanceDownloadDrawer.d';

type ExportReportCheckboxField = {
  name: ContentPerformanceExportFileKey;
  label: string;
  description: string;
};

const totalsFields: readonly ExportReportCheckboxField[] = [
  {
    name: 'episode-totals',
    label: 'Downloads & views total',
    description:
      'Network, Show, Episode, Status, Publish date, Downloads & views, Audio downloads, Audio downloads %, Video views, Video views %',
  },
  {
    name: 'episode-platforms',
    label: 'Downloads & views per app & website',
    description:
      'Network, Show, Episode, App or website, Audio & video, Audio, Audio %, Video, Video %',
  },
];

const postLaunchFields: readonly ExportReportCheckboxField[] = [
  {
    name: 'launch-first-7',
    label: 'First 7 days',
    description:
      'Network, Show, Episode, Status, Publish date, Downloads & views, Audio downloads, Audio downloads %, Video views, Video views %',
  },
  {
    name: 'launch-first-30',
    label: 'First 30 days',
    description:
      'Network, Show, Episode, Status, Publish date, Downloads & views, Audio downloads, Audio downloads %, Video views, Video views %',
  },
];

const reportFields = [...totalsFields, ...postLaunchFields] as const;
const includeKeys = reportFields.map((field) => field.name);

const renderCheckboxWithDescription = ({
  id,
  label,
  description,
}: {
  id: string;
  label: string;
  description: string;
}) => (
  <Flex key={id} vertical gap={4}>
    <Checkbox value={id} name={id}>
      {label}
    </Checkbox>
    <Typography.Text type="secondary" style={{ fontSize: 14, paddingLeft: 24 }}>
      {description}
    </Typography.Text>
  </Flex>
);

/**
 * Echo checkbox group for which content performance CSV files to include in the ZIP download.
 */
/**
 * @deprecated Legacy `files=` report checkboxes.
 * TODO: remove once `newDownloadDrawers` is enabled for everyone.
 */
const ContentPerformanceReportsSectionsEcho = ({
  downloadSettings,
  setDownloadSettings,
}: ContentPerformanceReportsSectionProps) => {
  const checkedValues = includeKeys.filter((key) => downloadSettings.include[key]);

  const handleGroupChange = (nextChecked: string[]) => {
    setDownloadSettings((previous: ContentPerformanceDownloadSettingsState) => ({
      ...previous,
      include: {
        ...previous.include,
        'episode-totals': nextChecked.includes('episode-totals'),
        'episode-platforms': nextChecked.includes('episode-platforms'),
        'launch-first-7': nextChecked.includes('launch-first-7'),
        'launch-first-30': nextChecked.includes('launch-first-30'),
      },
    }));
  };

  return (
    <DrawerSectionEcho title="Reports to download" aria-label="Reports to download">
      <Checkbox.Group value={checkedValues} onChange={handleGroupChange}>
        <Typography.Title level={5} aria-label="Totals for selected period">
          Totals for selected period
        </Typography.Title>
        <Flex vertical gap={12}>
          {totalsFields.map((field) =>
            renderCheckboxWithDescription({
              id: field.name,
              label: field.label,
              description: field.description,
            })
          )}
        </Flex>
        <Typography.Title level={5} aria-label="Post launch comparison" style={{ marginTop: 24 }}>
          Post launch comparison
        </Typography.Title>
        <Flex vertical gap={12}>
          {postLaunchFields.map((field) =>
            renderCheckboxWithDescription({
              id: field.name,
              label: field.label,
              description: field.description,
            })
          )}
        </Flex>
      </Checkbox.Group>
    </DrawerSectionEcho>
  );
};

export default ContentPerformanceReportsSectionsEcho;
